import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';

import { createTaskReminder, updateTaskReminder } from '../services/notificationService';
import type { TaskReminder, CreateTaskReminderData, UpdateTaskReminderData } from '../types/notification';

interface TaskReminderDialogProps {
  open: boolean;
  onClose: () => void;
  agencySlug: string;
  workflowId: string;
  propertyId: string;
  stageIndex: number;
  taskIndex: number;
  taskName: string;
  taskDescription: string;
  existingReminder?: TaskReminder;
  onSuccess?: () => void;
}

const TaskReminderDialog: React.FC<TaskReminderDialogProps> = ({
  open,
  onClose,
  agencySlug,
  workflowId,
  propertyId,
  stageIndex,
  taskIndex,
  taskName,
  taskDescription,
  existingReminder,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateTaskReminderData>({
    workflowId,
    propertyId,
    stageIndex,
    taskIndex,
    title: taskName,
    description: taskDescription,
    reminderTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Default to tomorrow
    isRecurring: false,
    notificationType: 'in_app',
    priority: 'medium'
  });

  const [recurringPattern, setRecurringPattern] = useState({
    type: 'daily' as 'daily' | 'weekly' | 'monthly',
    interval: 1,
    endDate: ''
  });

  useEffect(() => {
    if (existingReminder) {
      setFormData({
        workflowId: existingReminder.workflowId._id,
        propertyId: existingReminder.propertyId._id,
        stageIndex: existingReminder.stageIndex,
        taskIndex: existingReminder.taskIndex,
        title: existingReminder.title,
        description: existingReminder.description,
        reminderTime: existingReminder.reminderTime,
        isRecurring: existingReminder.isRecurring,
        notificationType: existingReminder.notificationType,
        priority: existingReminder.priority
      });

      if (existingReminder.recurringPattern) {
        setRecurringPattern({
          type: existingReminder.recurringPattern.type,
          interval: existingReminder.recurringPattern.interval,
          endDate: existingReminder.recurringPattern.endDate || ''
        });
      }
    } else {
      setFormData({
        workflowId,
        propertyId,
        stageIndex,
        taskIndex,
        title: taskName,
        description: taskDescription,
        reminderTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        isRecurring: false,
        notificationType: 'in_app',
        priority: 'medium'
      });
      setRecurringPattern({
        type: 'daily',
        interval: 1,
        endDate: ''
      });
    }
  }, [existingReminder, workflowId, propertyId, stageIndex, taskIndex, taskName, taskDescription]);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        ...(formData.isRecurring && {
          recurringPattern: {
            type: recurringPattern.type,
            interval: recurringPattern.interval,
            ...(recurringPattern.endDate && { endDate: recurringPattern.endDate })
          }
        })
      };

      if (existingReminder) {
        await updateTaskReminder(agencySlug, existingReminder._id, submitData as UpdateTaskReminderData);
      } else {
        await createTaskReminder(agencySlug, submitData);
      }

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    switch (type) {
      case 'email':
        return 'Email';
      case 'in_app':
        return 'In-App';
      case 'both':
        return 'Email & In-App';
      default:
        return type;
    }
  };

  // Helper function to convert ISO string to local datetime-local format
  const isoToLocalDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to convert local datetime-local format to ISO string
  const localDateTimeToIso = (localDateTime: string): string => {
    // The datetime-local input gives us a string like "2024-01-01T14:00"
    // We need to create an ISO string that represents this exact local time
    
    // Parse the local datetime components
    const [datePart, timePart] = localDateTime.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Create a date object in local time
    const localDate = new Date(year, month - 1, day, hours, minutes);
    
    // Convert to ISO string (this will be the correct UTC representation)
    return localDate.toISOString();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingReminder ? 'Edit Task Reminder' : 'Set Task Reminder'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Task: {taskName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {taskDescription}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reminder Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reminder Time"
              type="datetime-local"
              value={isoToLocalDateTime(formData.reminderTime)}
              onChange={(e) => {
                if (e.target.value) {
                  const isoString = localDateTimeToIso(e.target.value);
                  setFormData(prev => ({ 
                    ...prev, 
                    reminderTime: isoString
                  }));
                }
              }}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  priority: e.target.value as any 
                }))}
              >
                <MenuItem value="low">
                  <Chip label="Low" size="small" color="default" />
                </MenuItem>
                <MenuItem value="medium">
                  <Chip label="Medium" size="small" color="info" />
                </MenuItem>
                <MenuItem value="high">
                  <Chip label="High" size="small" color="warning" />
                </MenuItem>
                <MenuItem value="urgent">
                  <Chip label="Urgent" size="small" color="error" />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={formData.notificationType}
                label="Notification Type"
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  notificationType: e.target.value as any 
                }))}
              >
                <MenuItem value="in_app">In-App</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="both">Email & In-App</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRecurring}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    isRecurring: e.target.checked 
                  }))}
                />
              }
              label="Recurring Reminder"
            />
          </Grid>

          {formData.isRecurring && (
            <>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={recurringPattern.type}
                    label="Type"
                    onChange={(e) => setRecurringPattern(prev => ({ 
                      ...prev, 
                      type: e.target.value as any 
                    }))}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Interval"
                  type="number"
                  value={recurringPattern.interval}
                  onChange={(e) => setRecurringPattern(prev => ({ 
                    ...prev, 
                    interval: parseInt(e.target.value) || 1 
                  }))}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="End Date (Optional)"
                  type="date"
                  value={recurringPattern.endDate}
                  onChange={(e) => setRecurringPattern(prev => ({ 
                    ...prev, 
                    endDate: e.target.value 
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.title || !formData.reminderTime}
        >
          {loading ? <CircularProgress size={20} /> : (existingReminder ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskReminderDialog; 